import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { extras } from "../i18n";
import { API } from "../config";
import { Camera, Upload, X, Loader2, ImageOff, Star } from "lucide-react";
import { FaInstagram } from "react-icons/fa";

const VOTES_KEY = "topacio_votes";
const VOTER_KEY = "topacio_voter";

function getVoter() {
  let v = localStorage.getItem(VOTER_KEY);
  if (!v) {
    v = Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(VOTER_KEY, v);
  }
  return v;
}

function getMyVotes() {
  try { return JSON.parse(localStorage.getItem(VOTES_KEY) || "{}"); } catch { return {}; }
}

function StarRating({ photo, onVoted }) {
  const { lang } = useLang();
  const x = extras[lang] || extras.es;
  const [hover, setHover] = useState(0);
  const myVotes = getMyVotes();
  const myVote = myVotes[photo.id];

  const vote = async (stars) => {
    if (myVote) return;
    try {
      const { data } = await axios.post(`${API}/gallery/${photo.id}/vote`, { stars, voter: getVoter() });
      localStorage.setItem(VOTES_KEY, JSON.stringify({ ...getMyVotes(), [photo.id]: stars }));
      toast.success(x.thanksVote);
      onVoted(photo.id, data.avg, data.count, stars);
    } catch (e) {
      if (e.response?.status === 409) {
        localStorage.setItem(VOTES_KEY, JSON.stringify({ ...getMyVotes(), [photo.id]: 1 }));
        toast.error(x.alreadyVoted);
      } else {
        toast.error(x.voteError);
      }
    }
  };

  const display = myVote || Math.round(photo.avg || 0);
  return (
    <div className="flex items-center gap-1" data-testid={`stars-${photo.id}`}>
      {[1, 2, 3, 4, 5].map((s) => (
        <button key={s} data-testid={`star-${photo.id}-${s}`}
          disabled={!!myVote}
          onMouseEnter={() => !myVote && setHover(s)}
          onMouseLeave={() => setHover(0)}
          onClick={() => vote(s)}
          className={`transition-transform ${myVote ? "cursor-default" : "cursor-pointer hover:scale-125"}`}>
          <Star className={`w-4 h-4 ${(hover || display) >= s ? "fill-brand text-topacio-ink" : "text-zinc-300"}`} strokeWidth={2} />
        </button>
      ))}
      <span className="ml-1.5 text-[11px] font-extrabold text-topacio-faded">
        {photo.avg > 0 ? `${photo.avg} · ${photo.votes_count} ${x.votes}` : `0 ${x.votes}`}
      </span>
    </div>
  );
}

export default function Gallery() {
  const { t, lang } = useLang();
  const x = extras[lang] || extras.es;
  const [photos, setPhotos] = useState([]);
  const [open, setOpen] = useState(false);
  const [ref, setRef] = useState("");
  const [email, setEmail] = useState("");
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [rights, setRights] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => {
    axios.get(`${API}/gallery`).then(({ data }) => setPhotos(data.photos)).catch(() => {});
  }, []);

  const onVoted = (id, avg, count) => {
    setPhotos((prev) => prev.map((p) => (p.id === id ? { ...p, avg, votes_count: count } : p)));
  };

  const pick = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error(t.gallery.badType);
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error(t.gallery.tooBig);
      return;
    }
    setFile(f);
  };

  const submit = async () => {
    if (!ref || !email || !file) return;
    if (!rights) {
      toast.error(x.rightsRequired);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("booking_ref", ref);
      fd.append("email", email);
      fd.append("caption", caption);
      fd.append("rights", "true");
      fd.append("file", file);
      await axios.post(`${API}/gallery/upload`, fd);
      toast.success(t.gallery.sent);
      setOpen(false);
      setRef(""); setEmail(""); setCaption(""); setFile(null); setRights(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "booking_not_found") toast.error(t.gallery.verifiedErr);
      else if (detail === "bad_type") toast.error(t.gallery.badType);
      else if (detail === "too_big") toast.error(t.gallery.tooBig);
      else if (detail === "rights_required") toast.error(x.rightsRequired);
      else toast.error(t.booking.error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="galeria" className="border-y-2 border-topacio-ink bg-white">
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <p className="eyebrow mb-4">{t.gallery.eyebrow}</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-topacio-ink">{t.gallery.title}</h2>
            <p className="mt-3 text-topacio-faded max-w-xl">{t.gallery.sub}</p>
          </div>
          <button data-testid="gallery-upload-btn" onClick={() => setOpen(true)} className="btn-primary !text-xs">
            <Camera className="w-4 h-4" /> {t.gallery.upload}
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {photos.map((p, i) => (
            <div key={p.id} data-testid={`gallery-photo-${i}`}
              className={`card-brutal overflow-hidden hover:-translate-y-1 hover:shadow-[8px_8px_0_0_#111] transition-[transform,box-shadow] ${i % 2 ? "rotate-1" : "-rotate-1"} hover:rotate-0`}>
              {p.kind === "video" ? (
                <video src={p.static_url} poster={p.poster_url || undefined} controls preload="metadata" playsInline
                  data-testid={`gallery-video-${i}`}
                  className="w-full aspect-square object-cover border-b-2 border-topacio-ink bg-black" />
              ) : (
                <img src={p.static_url || `${API}/gallery/image/${p.id}`} alt={p.caption || `Foto de ${p.name}`} loading="lazy"
                  className="w-full aspect-square object-cover border-b-2 border-topacio-ink" />
              )}
              <div className="px-4 py-3 bg-white">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-extrabold text-topacio-ink truncate">{p.name}</p>
                </div>
                {p.caption && <p className="text-[11px] text-topacio-faded mt-0.5 truncate">{p.caption}</p>}
                <div className="mt-2">
                  <StarRating photo={p} onVoted={onVoted} />
                </div>
              </div>
            </div>
          ))}
          {photos.length === 0 && (
            <div className="card-brutal p-12 text-center sm:col-span-2 lg:col-span-3">
              <ImageOff className="w-10 h-10 text-zinc-300 mx-auto" />
              <p className="text-topacio-faded text-sm mt-4">{t.gallery.empty}</p>
            </div>
          )}
          <a href="https://instagram.com/motostopacio" target="_blank" rel="noopener noreferrer"
            data-testid="instagram-card"
            className="card-brutal bg-brand p-6 flex flex-col justify-between hover:-translate-y-1 hover:shadow-[8px_8px_0_0_#111] transition-[transform,box-shadow] min-h-[220px]">
            <FaInstagram className="w-10 h-10 text-topacio-ink" />
            <div>
              <p className="font-display font-black text-topacio-ink text-lg leading-tight">{x.followInsta}</p>
              <p className="text-sm font-semibold text-topacio-ink/70 mt-1">@motostopacio</p>
              <p className="text-xs text-topacio-ink/60 mt-2">{x.instaSub}</p>
            </div>
          </a>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="gallery-upload-modal">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative card-brutal w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" data-lenis-prevent>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display font-bold text-topacio-ink">{t.gallery.uploadTitle}</h3>
              <button data-testid="gallery-close-btn" onClick={() => setOpen(false)} className="text-topacio-faded hover:text-topacio-ink">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-topacio-faded font-extrabold mb-2">{t.gallery.refLabel}</label>
                <input data-testid="gallery-ref-input" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="RMT-XXXXXX" className="input-brutal" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-topacio-faded font-extrabold mb-2">{t.gallery.emailLabel}</label>
                <input data-testid="gallery-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-brutal" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-topacio-faded font-extrabold mb-2">{x.captionLabel}</label>
                <input data-testid="gallery-caption-input" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder={x.captionPh} className="input-brutal" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-topacio-faded font-extrabold mb-2">{t.gallery.photoLabel}</label>
                <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" onChange={pick} className="hidden" />
                <button data-testid="gallery-file-btn" onClick={() => fileInput.current.click()}
                  className="w-full input-brutal border-dashed flex items-center justify-center gap-2 py-6 text-topacio-faded hover:text-topacio-ink">
                  <Upload className="w-4 h-4" />
                  {file ? file.name : t.gallery.selectPhoto}
                </button>
              </div>
              <label data-testid="gallery-rights-label" className="flex items-start gap-3 cursor-pointer select-none bg-topacio-sand border-2 border-dashed border-topacio-ink/30 rounded-lg p-3.5">
                <input type="checkbox" data-testid="gallery-rights-checkbox" checked={rights}
                  onChange={(e) => setRights(e.target.checked)}
                  className="mt-0.5 w-4 h-4 shrink-0 accent-[#E63946] cursor-pointer" />
                <span className="text-[11px] leading-snug text-topacio-faded font-semibold">{x.rightsText}</span>
              </label>
              <button data-testid="gallery-submit-btn" disabled={busy || !ref || !email || !file || !rights} onClick={submit} className="btn-primary w-full">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {t.gallery.send}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
